import React, { useState, useEffect } from 'react';
import { 
  X, Clock, Shield, Trash2, 
  Users, MapPin, Settings2, LayoutGrid, 
  ChevronRight, History, Navigation, Lock, Repeat, Bell
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/Button';
import { AppState, CalendarEvent, HouseholdMember } from '@/types';
import { isValid, parseISO } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import { GOOGLE_EVENT_COLORS, DEFAULT_EVENT_COLOR, colorIdForHex } from '../utils/googleCalendarColors';

// Local interface for the simplified source type
type CalendarSource = 'personal' | 'household';

interface EventModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (eventData: any) => Promise<void>;
  onDelete?: (id: string, scope: 'personal' | 'household') => void;
  initialData: Partial<CalendarEvent> & {
    googleEventId?: string;
    etag?: string;
    status?: string;
    htmlLink?: string;
    iCalUID?: string;
    recurrence?: string[];
    recurringEventId?: string;
    originalStartTime?: { dateTime: string; timeZone: string; };
    transparency?: string;
    visibility?: string;
    organizer?: { email: string; displayName: string; self: boolean; };
    attendees?: Array<{ email: string; responseStatus: string; comment?: string; }>;
    hangoutLink?: string;
    creatorId?: string;
    templateType?: string;
    travelMetadata?: {
      estimatedDuration: number;
      originType: "home" | "previous_event";
      isImpossible: boolean;
      travelBackHomeAdvised: boolean;
    };
    reminders?: { useDefault: boolean; overrides: Array<{ method: string; minutes: number; }> };
    isAllDay?: boolean;
    category?: string;
    start?: any;
    end?: any;
    title?: string;
    description?: string;
    id?: string;
  };
  userTimezone: string;
  state: AppState;
  contacts: any[]; 
  defaultScope?: 'personal' | 'household';
  householdMembers: HouseholdMember[];
}

const EVENT_CATEGORIES = [
  'General', 'Maintenance', 'Groceries', 'Appointments', 'Chores', 'Social', 'Work', 'School', 'Shopping', 'Health', 'Finance', 'Travel'
];

const RECURRENCE_OPTIONS = [
  { value: '', label: 'Does not repeat' },
  { value: 'FREQ=DAILY', label: 'Daily' },
  { value: 'FREQ=WEEKLY', label: 'Weekly' },
  { value: 'FREQ=MONTHLY', label: 'Monthly' },
  { value: 'FREQ=YEARLY', label: 'Yearly' },
];

const REMINDER_OPTIONS = [
  { value: 0, label: 'At time of event' },
  { value: 10, label: '10 minutes before' },
  { value: 30, label: '30 minutes before' },
  { value: 60, label: '1 hour before' },
  { value: 1440, label: '1 day before' },
];

export const EventModal: React.FC<EventModalProps> = ({
  isOpen,
  onClose,
  onSave,
  onDelete,
  initialData,
  userTimezone,
  state,
  contacts,
  householdMembers,
}) => {
  const { t } = useTranslation();
  const currentHouseholdId = state.currentHouseholdId;
  
  const [viewMode, setViewMode] = useState<'simple' | 'advanced'>(
    (localStorage.getItem('calendar_view_pref') as 'simple' | 'advanced') || 'simple'
  );

  const toggleViewMode = () => {
    const newMode = viewMode === 'simple' ? 'advanced' : 'simple';
    setViewMode(newMode);
    localStorage.setItem('calendar_view_pref', newMode);
  };

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [source, setSource] = useState<CalendarSource>('personal');
  const [startDateStr, setStartDateStr] = useState('');
  const [endDateStr, setEndDateStr] = useState('');
  const [color, setColor] = useState(DEFAULT_EVENT_COLOR.hex);
  const [isAllDay, setIsAllDay] = useState(false);
  const [category, setCategory] = useState<string>(EVENT_CATEGORIES[0]);
  const [selectedRecurrence, setSelectedRecurrence] = useState<string>('');
  const [selectedReminder, setSelectedReminder] = useState<number>(REMINDER_OPTIONS[0].value);
  
  const [address, setAddress] = useState('');
  const [assignedContactId, setAssignedContactId] = useState('');
  const [googleEventId, setGoogleEventId] = useState<string | undefined>(undefined);
  const [etag, setEtag] = useState<string | undefined>(undefined);
  const [status, setStatus] = useState<string>('confirmed');
  const [htmlLink, setHtmlLink] = useState<string | undefined>(undefined);
  const [iCalUID, setICalUID] = useState<string | undefined>(undefined);
  const [recurringEventId, setRecurringEventId] = useState<string | undefined>(undefined);
  const [originalStartTime, setOriginalStartTime] = useState<{ dateTime: string; timeZone: string; } | undefined>(undefined);
  const [transparency, setTransparency] = useState<string>('opaque');
  const [visibility, setVisibility] = useState<string>('default');
  const [organizer, setOrganizer] = useState<{ email: string; displayName: string; self: boolean; } | undefined>(undefined);
  const [attendees, setAttendees] = useState<Array<{ email: string; displayName?: string; self?: boolean; responseStatus: string; comment?: string; }>>([]);
  const [hangoutLink, setHangoutLink] = useState<string | undefined>(undefined);
  const [creatorId, setCreatorId] = useState<string | undefined>(undefined);
  const [templateType, setTemplateType] = useState<string | undefined>(undefined);
  const [travelMetadata, setTravelMetadata] = useState<{
    estimatedDuration: number;
    originType: "home" | "previous_event";
    isImpossible: boolean;
    travelBackHomeAdvised: boolean;
  } | undefined>(undefined);
  const [arrivalBufferMins, setArrivalBufferMins] = useState<number>(15);

  useEffect(() => {
    if (isOpen && initialData) {
      setTitle(initialData.title || '');
      setDescription(initialData.description || '');
      
      const initialSource = (initialData as any).source || (currentHouseholdId ? 'household' : 'personal');
      setSource(initialSource);
      
      setColor((initialData as any).color || DEFAULT_EVENT_COLOR.hex);
      setAddress((initialData as any).location?.address || '');
      setAssignedContactId((initialData as any).assignedContactId || '');
      setGoogleEventId(initialData.googleEventId);
      setEtag(initialData.etag);
      setStatus(initialData.status || 'confirmed');
      setHtmlLink(initialData.htmlLink);
      setICalUID(initialData.iCalUID);
      setSelectedRecurrence(initialData.recurrence?.[0] || '');
      setRecurringEventId(initialData.recurringEventId);
      setOriginalStartTime(initialData.originalStartTime);
      setTransparency(initialData.transparency || 'opaque');
      setVisibility(initialData.visibility || 'default');
      setOrganizer(initialData.organizer);
      
      if (initialData.attendees) {
        const updatedAttendees = initialData.attendees.map(att => {
          const member = householdMembers.find(m => m.email === att.email);
          return {
            ...att,
            displayName: member?.name || att.email,
          };
        });
        setAttendees(updatedAttendees);
      } else {
        setAttendees([]);
      }
      
      setHangoutLink(initialData.hangoutLink);
      setCreatorId(initialData.creatorId);
      setTemplateType(initialData.templateType);
      setTravelMetadata(initialData.travelMetadata);

      setIsAllDay(initialData.isAllDay || false);
      setCategory(initialData.category || EVENT_CATEGORIES[0]);
      setArrivalBufferMins((initialData as any).arrivalBufferMins ?? 15);
      
      if (initialData.reminders?.overrides?.[0]?.minutes !== undefined) {
        setSelectedReminder(initialData.reminders.overrides[0].minutes);
      } else {
        setSelectedReminder(REMINDER_OPTIONS[0].value);
      }
      
      const normalize = (input: any) => {
        if (!input) return '';
        const d = typeof input === 'string' ? parseISO(input) : input;
        return isValid(d) ? formatInTimeZone(d, userTimezone, "yyyy-MM-dd'T'HH:mm") : '';
      };

      const normalizeDateOnly = (input: any) => {
        if (!input) return '';
        const d = typeof input === 'string' ? parseISO(input) : input;
        return isValid(d) ? formatInTimeZone(d, userTimezone, "yyyy-MM-dd") : '';
      };

      setStartDateStr(initialData.isAllDay ? normalizeDateOnly(initialData.start) : normalize(initialData.start));
      setEndDateStr(initialData.isAllDay ? normalizeDateOnly(initialData.end) : normalize(initialData.end));
    }
  }, [isOpen, initialData, userTimezone, currentHouseholdId, householdMembers]);

  const handleSave = () => {
    onSave({
      ...initialData,
      title,
      description,
      source, 
      start: startDateStr,
      end: endDateStr,
      color,
      location: address ? { address } : null,
      assignedContactId: assignedContactId || null,
      isSuggestion: false,
      localChangeTimestamp: Date.now(),
      colorId: colorIdForHex(color),
      googleEventId,
      etag,
      status,
      htmlLink,
      iCalUID,
      recurrence: selectedRecurrence ? [selectedRecurrence] : [],
      recurringEventId,
      originalStartTime,
      transparency,
      visibility,
      organizer,
      attendees: attendees.map(att => ({ email: att.email, responseStatus: att.responseStatus || 'needsAction' })),
      hangoutLink,
      creatorId,
      templateType,
      travelMetadata,
      arrivalBufferMins,
      reminders: selectedReminder !== 0
        ? { useDefault: false, overrides: [{ method: 'popup', minutes: selectedReminder }] }
        : { useDefault: true, overrides: [] },
      isAllDay,
      category,
    });
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[120] flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-md animate-in fade-in duration-200">
      <div className="bg-white dark:bg-slate-900 w-full max-w-2xl rounded-[2rem] shadow-2xl border border-slate-200 dark:border-slate-800 flex flex-col max-h-[90vh] overflow-hidden">
        
        <div className="p-8 border-b border-slate-100 dark:border-slate-800 flex justify-between items-center bg-white dark:bg-slate-900">
          <div className="flex items-center gap-4">
            <div className={`p-3 rounded-2xl transition-colors ${viewMode === 'advanced' ? 'bg-brand text-white' : 'bg-slate-100 dark:bg-slate-800 text-slate-400'}`}>
              {viewMode === 'advanced' ? <Settings2 className="w-6 h-6" /> : <LayoutGrid className="w-6 h-6" />}
            </div>
            <div>
              <h3 className="text-2xl font-black text-slate-900 dark:text-white tracking-tighter uppercase italic leading-none">
                {initialData?.id ? t('cal.edit', 'Modify Event') : t('cal.new', 'New Entry')}
              </h3>
              <button 
                onClick={toggleViewMode}
                className="text-2xs font-black text-brand uppercase tracking-widest flex items-center gap-1 mt-1 hover:text-brand"
              >
                {viewMode === 'simple' ? t('cal.switch_advanced', 'Switch to Advanced View') : t('cal.switch_simple', 'Return to Simple View')}
                <ChevronRight className="w-3 h-3" />
              </button>
            </div>
          </div>
          <button onClick={onClose} className="p-3 hover:bg-slate-50 dark:hover:bg-slate-800 rounded-full transition-colors">
            <X className="w-6 h-6 text-slate-300" />
          </button>
        </div>

        <div className="p-8 space-y-8 overflow-y-auto no-scrollbar flex-1">
          <div className="space-y-4">
            <input 
              autoFocus
              placeholder={t('cal.title_placeholder', 'What is happening?')}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              className="w-full text-5xl font-black bg-transparent border-none focus:ring-0 placeholder:text-slate-300 dark:placeholder:text-slate-600 dark:text-white outline-none tracking-tighter uppercase italic"
            />
            
            <div className="flex flex-wrap items-center gap-2">
              <SourceBadge 
                active={source === 'personal'} 
                label={t('cal.personal_private', 'Personal (Private)')} 
                icon={<Shield className="w-3 h-3"/>} 
                onClick={() => setSource('personal')} 
              />
              <SourceBadge 
                active={source === 'household'} 
                label={t('cal.household_shared', 'Household (Shared)')} 
                icon={<Users className="w-3 h-3"/>} 
                disabled={!currentHouseholdId}
                onClick={() => setSource('household')} 
              />
              {!currentHouseholdId && (
                <div className="flex items-center gap-1.5 ml-2">
                  <Lock className="w-2.5 h-2.5 text-amber-500" />
                  <span className="text-[8px] font-black uppercase text-amber-500 tracking-tighter">{t('cal.shared_mode_req', 'Shared Mode Requires Household Node')}</span>
                </div>
              )}
            </div>

            <div className="flex items-center gap-2 mt-4">
              <input
                type="checkbox"
                id="all-day-toggle"
                checked={isAllDay}
                onChange={(e) => {
                  setIsAllDay(e.target.checked);
                  const sd = isValid(parseISO(startDateStr)) ? parseISO(startDateStr) : new Date();
                  const ed = isValid(parseISO(endDateStr)) ? parseISO(endDateStr) : new Date();
                  if (e.target.checked) {
                    setStartDateStr(formatInTimeZone(sd, userTimezone, "yyyy-MM-dd"));
                    setEndDateStr(formatInTimeZone(ed, userTimezone, "yyyy-MM-dd"));
                  } else {
                    setStartDateStr(formatInTimeZone(sd, userTimezone, "yyyy-MM-dd'T'HH:mm"));
                    setEndDateStr(formatInTimeZone(ed, userTimezone, "yyyy-MM-dd'T'HH:mm"));
                  }
                }}
                className="w-4 h-4 text-brand border-gray-300 rounded focus:ring-brand"
              />
              <label htmlFor="all-day-toggle" className="text-sm font-medium text-slate-700 dark:text-slate-300">
                {t('cal.all_day', 'All Day Event')}
              </label>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <label className="text-2xs font-black uppercase text-slate-400 ml-4 tracking-widest flex items-center gap-2">
                  <Clock className="w-3 h-3" /> {t('cal.start', 'Start Time')}
                </label>
                <input 
                  type={isAllDay ? "date" : "datetime-local"} 
                  value={startDateStr} 
                  onChange={(e) => setStartDateStr(e.target.value)} 
                  className="w-full p-5 bg-slate-50 dark:bg-slate-800 dark:text-white rounded-3xl border-none font-bold text-sm focus:ring-2 ring-brand/20" 
                />
              </div>
              <div className="space-y-2">
                <label className="text-2xs font-black uppercase text-slate-400 ml-4 tracking-widest flex items-center gap-2">
                  <Clock className="w-3 h-3" /> {t('cal.end', 'End Time')}
                </label>
                <input 
                  type={isAllDay ? "date" : "datetime-local"} 
                  value={endDateStr} 
                  onChange={(e) => setEndDateStr(e.target.value)} 
                  className="w-full p-5 bg-slate-50 dark:bg-slate-800 dark:text-white rounded-3xl border-none font-bold text-sm focus:ring-2 ring-brand/20" 
                />
              </div>
            </div>

            {viewMode === 'advanced' && (
              <div className="space-y-8 animate-in slide-in-from-top-4 duration-300">
                <div className="h-px bg-slate-100 dark:bg-slate-800" />
                
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <label className="text-2xs font-black uppercase text-slate-400 ml-4 tracking-widest">
                      {t('cal.status', 'Status')}
                    </label>
                    <select
                      value={status}
                      onChange={(e) => setStatus(e.target.value)}
                      className="w-full p-5 bg-slate-50 dark:bg-slate-800 dark:text-white rounded-[2rem] border-none font-bold text-sm"
                    >
                      <option value="confirmed">{t('cal.status_confirmed', 'Confirmed')}</option>
                      <option value="tentative">{t('cal.status_tentative', 'Tentative')}</option>
                      <option value="cancelled">{t('cal.status_cancelled', 'Cancelled')}</option>
                    </select>
                  </div>
                  <div className="space-y-2">
                    <label className="text-2xs font-black uppercase text-slate-400 ml-4 tracking-widest">
                      {t('cal.transparency', 'Transparency')}
                    </label>
                    <select
                      value={transparency}
                      onChange={(e) => setTransparency(e.target.value)}
                      className="w-full p-5 bg-slate-50 dark:bg-slate-800 dark:text-white rounded-[2rem] border-none font-bold text-sm"
                    >
                      <option value="opaque">{t('cal.transparency_opaque', 'Opaque (Busy)')}</option>
                      <option value="transparent">{t('cal.transparency_transparent', 'Transparent (Free)')}</option>
                    </select>
                  </div>
                  <div className="space-y-2">
                    <label className="text-2xs font-black uppercase text-slate-400 ml-4 tracking-widest">
                      {t('cal.visibility', 'Visibility')}
                    </label>
                    <select
                      value={visibility}
                      onChange={(e) => setVisibility(e.target.value)}
                      className="w-full p-5 bg-slate-50 dark:bg-slate-800 dark:text-white rounded-[2rem] border-none font-bold text-sm"
                    >
                      <option value="default">{t('cal.visibility_default', 'Default')}</option>
                      <option value="public">{t('cal.visibility_public', 'Public')}</option>
                      <option value="private">{t('cal.visibility_private', 'Private')}</option>
                      <option value="confidential">{t('cal.visibility_confidential', 'Confidential')}</option>
                    </select>
                  </div>
                </div>

                <div className="space-y-4">
                  <label className="text-2xs font-black uppercase text-brand ml-4 tracking-[0.2em] flex items-center gap-2">
                    <Navigation className="w-3 h-3" /> {t('cal.geospatial', 'Geospatial Data')}
                  </label>
                  <div className="relative">
                    <MapPin className="absolute left-5 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                    <input
                      placeholder={t('cal.address', 'Event Location / Address')}
                      value={address}
                      onChange={(e) => setAddress(e.target.value)}
                      className="w-full pl-14 pr-6 py-5 bg-slate-50 dark:bg-slate-800 dark:text-white rounded-[2rem] border-none font-bold text-sm"
                    />
                  </div>
                  {address && (
                    <div className="flex items-center gap-4">
                      <label className="text-2xs font-black uppercase text-slate-400 tracking-widest whitespace-nowrap">
                        {t('cal.arrival_buffer', 'Arrival Buffer (min)')}
                      </label>
                      <input
                        type="number"
                        min={0}
                        max={120}
                        value={arrivalBufferMins}
                        onChange={(e) => setArrivalBufferMins(parseInt(e.target.value) || 0)}
                        className="w-24 p-3 bg-slate-50 dark:bg-slate-800 dark:text-white rounded-2xl border-none font-bold text-sm text-center"
                      />
                    </div>
                  )}
                </div>

                {currentHouseholdId && source === 'household' && (
                  <div className="space-y-4">
                    <label className="text-2xs font-black uppercase text-slate-400 ml-4 tracking-widest flex items-center gap-2">
                      <History className="w-3 h-3" /> {t('cal.assign_pro', 'Assign Professional')}
                    </label>
                    <select 
                      value={assignedContactId}
                      onChange={(e) => setAssignedContactId(e.target.value)}
                      className="w-full p-5 bg-slate-50 dark:bg-slate-800 dark:text-white rounded-[2rem] border-none font-bold text-sm"
                    >
                      <option value="">{t('cal.no_pro', 'No Service Provider Assigned')}</option>
                      {contacts.map((contact: any) => (
                        <option key={contact.id} value={contact.id}>{contact.name} ({contact.subCategory})</option>
                      ))}
                    </select>
                  </div>
                )}
                
                <div className="space-y-2">
                  <label className="text-2xs font-black uppercase text-slate-400 ml-4 tracking-widest">{t('cal.label', 'Color')}</label>
                  <div className="flex flex-wrap gap-2 px-1">
                    {GOOGLE_EVENT_COLORS.map(c => (
                      <button
                        key={c.id}
                        type="button"
                        title={c.name}
                        onClick={() => setColor(c.hex)}
                        className={`w-8 h-8 rounded-full transition-all ${color.toLowerCase() === c.hex.toLowerCase() ? 'ring-2 ring-offset-4 ring-brand scale-110' : 'opacity-40 hover:opacity-70'}`}
                        style={{ backgroundColor: c.hex }}
                      />
                    ))}
                  </div>
                  {!GOOGLE_EVENT_COLORS.some(c => c.hex.toLowerCase() === color.toLowerCase()) && (
                    <p className="text-[9px] font-bold text-amber-500 uppercase tracking-wider ml-4">
                      {t('cal.custom_color_warning', 'Custom color — won\'t match a Google Calendar color if this event is synced')}
                    </p>
                  )}
                </div>

                <div className="space-y-4">
                  <label className="text-2xs font-black uppercase text-brand ml-4 tracking-[0.2em] flex items-center gap-2">
                    <LayoutGrid className="w-3 h-3" /> {t('cal.category', 'Category')}
                  </label>
                  <select
                    value={category}
                    onChange={(e) => setCategory(e.target.value)}
                    className="w-full p-5 bg-slate-50 dark:bg-slate-800 dark:text-white rounded-[2rem] border-none font-bold text-sm"
                  >
                    {EVENT_CATEGORIES.map(cat => (
                      <option key={cat} value={cat}>{t(`cal.category_${cat.toLowerCase()}`, cat)}</option>
                    ))}
                  </select>
                </div>

                <div className="space-y-4">
                  <label className="text-2xs font-black uppercase text-brand ml-4 tracking-[0.2em] flex items-center gap-2">
                    <Repeat className="w-3 h-3" /> {t('cal.recurrence', 'Recurrence')}
                  </label>
                  <select
                    value={selectedRecurrence}
                    onChange={(e) => setSelectedRecurrence(e.target.value)}
                    className="w-full p-5 bg-slate-50 dark:bg-slate-800 dark:text-white rounded-[2rem] border-none font-bold text-sm"
                  >
                    {RECURRENCE_OPTIONS.map(option => (
                      <option key={option.value} value={option.value}>{t(`cal.recurrence_${option.label.toLowerCase().replace(/ /g, '_')}`, option.label)}</option>
                    ))}
                  </select>
                </div>

                <div className="space-y-4">
                  <label className="text-2xs font-black uppercase text-brand ml-4 tracking-[0.2em] flex items-center gap-2">
                    <Bell className="w-3 h-3" /> {t('cal.reminders', 'Reminders')}
                  </label>
                  <select
                    value={selectedReminder}
                    onChange={(e) => setSelectedReminder(parseInt(e.target.value))}
                    className="w-full p-5 bg-slate-50 dark:bg-slate-800 dark:text-white rounded-[2rem] border-none font-bold text-sm"
                  >
                    {REMINDER_OPTIONS.map(option => (
                      <option key={option.value} value={option.value}>{t(`cal.reminder_${option.label.toLowerCase().replace(/ /g, '_')}`, option.label)}</option>
                    ))}
                  </select>
                </div>

                {currentHouseholdId && source === 'household' && (
                  <div className="space-y-4">
                    <label className="text-2xs font-black uppercase text-brand ml-4 tracking-[0.2em] flex items-center gap-2">
                      <Users className="w-3 h-3" /> {t('cal.attendees', 'Attendees')}
                    </label>
                    <div className="flex flex-col gap-2 p-5 bg-slate-50 dark:bg-slate-800 dark:text-white rounded-[2rem] font-bold text-sm">
                      {householdMembers.map(member => (
                        <label key={member.userId} className="flex items-center gap-3 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={attendees.some(att => att.email === member.email)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setAttendees(prev => [
                                  ...prev,
                                  { email: member.email, displayName: member.name, responseStatus: 'needsAction' }
                                ]);
                              } else {
                                setAttendees(prev => prev.filter(att => att.email !== member.email));
                              }
                            }}
                            className="w-4 h-4 text-brand border-gray-300 rounded focus:ring-brand"
                          />
                          <span>{member.name} ({member.email})</span>
                        </label>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
          
          <div className="space-y-2">
            <textarea
              placeholder={t('cal.notes', 'Notes, links, or instructions...')}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              className="w-full p-6 bg-slate-50 dark:bg-slate-800 dark:text-white rounded-[2.5rem] border-none font-medium text-sm"
            />
          </div>
        </div>

        <footer className="p-8 bg-slate-50 dark:bg-slate-950 border-t border-slate-100 dark:border-slate-800 flex justify-between gap-4">
          {initialData?.id && (
            <button
              onClick={() => onDelete?.(initialData.id!, source)}
              className="p-4 text-rose-500 hover:bg-rose-50 dark:hover:bg-rose-900/20 rounded-2xl transition-colors"
            >
              <Trash2 className="w-6 h-6" />
            </button>
          )}
          <div className="flex gap-3 flex-1">
            <Button variant="ghost" onClick={onClose} className="flex-1 rounded-3xl h-14 font-black uppercase text-xs tracking-widest">
              Cancel
            </Button>
            <Button onClick={handleSave} className="flex-1 rounded-3xl h-14 bg-brand text-white shadow-xl shadow-brand/30 font-black uppercase text-xs tracking-widest">
              Save Event
            </Button>
          </div>
        </footer>
      </div>
    </div>
  );
};

interface SourceBadgeProps {
  active: boolean;
  label: string;
  icon: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
}

const SourceBadge: React.FC<SourceBadgeProps> = ({ active, label, icon, onClick, disabled }) => (
  <button 
    onClick={onClick}
    disabled={disabled}
    className={`px-4 py-2 rounded-xl flex items-center gap-2 text-2xs font-black uppercase tracking-widest transition-all ${
      disabled ? 'opacity-30 cursor-not-allowed bg-slate-100 dark:bg-slate-800 text-slate-400' :
      active ? 'bg-slate-900 dark:bg-white text-white dark:text-slate-900 shadow-lg' : 'bg-slate-100 dark:bg-slate-800 text-slate-400 hover:text-slate-600'
    }`}
  >
    {icon} {label}
  </button>
);